import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/components/common';
import type { ColDef } from '@/grid/types';
import { dateTimeFormatter } from '@/grid/value-formatters';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import type {
  OnFarmImplantFreezingEditableField,
  OnFarmImplantFreezingEmbryo,
} from '@/services/on-farm-implant';
import { todayWallClockDate } from '@/utils';

import { embryoStatusColors } from '../FrozenTab/columnDefs';
import { useSourceTypeLabel } from '../ImplantQueueTab/columnDefs';
import type { FreezingFieldOptions } from './useFreezingFieldOptions';

export const FREEZING_SELECT_BLANK = '';

export const FREEZING_TEXT_LIMITS = {
  cane: 20,
  freezer: 10,
} as const;

export const FREEZING_CELL_EDITABLE_FIELDS: OnFarmImplantFreezingEditableField[] =
  [
    'freezeDate',
    'freezeTechId',
    'freezeMethodId',
    'dtPlus',
    'strawNumber',
    'cane',
    'storageLocationId',
    'freezer',
    'graderId',
    'crackedZona',
  ];

type LookupField =
  | 'freezeTechId'
  | 'freezeMethodId'
  | 'storageLocationId'
  | 'graderId';

const LOOKUP_NAME_FIELDS: Record<
  LookupField,
  keyof OnFarmImplantFreezingEmbryo
> = {
  freezeTechId: 'freezeTechName',
  freezeMethodId: 'freezeMethodName',
  storageLocationId: 'storageLocationName',
  graderId: 'graderName',
};

type OnFarmFreezingColumnDefsOptions = {
  readOnly?: boolean;
  options?: FreezingFieldOptions;
};

export const useOnFarmFreezingColumnDefs = ({
  readOnly = true,
  options,
}: OnFarmFreezingColumnDefsOptions = {}): ColDef<OnFarmImplantFreezingEmbryo>[] => {
  const { t } = useTranslation();
  const sourceTypeLabel = useSourceTypeLabel();

  return useMemo(() => {
    const isCellEditable = () => !readOnly;
    const editableCellClassRules = { 'editable-cell': isCellEditable };
    const yesNo = (value: boolean | null | undefined) =>
      value == null
        ? ''
        : value
          ? t('onFarmImplant.onFarmFreezing.yes')
          : t('onFarmImplant.onFarmFreezing.no');

    const lookupColumn = (
      field: LookupField,
      headerName: string,
      lookupOptions: { value: string; label: string }[],
    ): ColDef<OnFarmImplantFreezingEmbryo> => {
      const labelFor = (
        value: string | null | undefined,
        row?: OnFarmImplantFreezingEmbryo,
      ) => {
        if (!value) return '';
        return (
          lookupOptions.find((option) => option.value === value)?.label ??
          ((row?.[LOOKUP_NAME_FIELDS[field]] as string | null | undefined) ||
            '')
        );
      };

      return {
        field,
        colId: field,
        headerName,
        flex: 1,
        minWidth: 150,
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: [
            FREEZING_SELECT_BLANK,
            ...lookupOptions.map((option) => option.value),
          ],
          formatValue: (value: string) =>
            value === FREEZING_SELECT_BLANK
              ? t('onFarmImplant.onFarmFreezing.none')
              : labelFor(value),
          allowTyping: true,
          filterList: true,
          searchType: 'matchAny',
          highlightMatch: true,
        },
        valueFormatter: (params) =>
          labelFor(params.value as string | null | undefined, params.data),
        filter: 'agSetColumnFilter',
        filterValueGetter: (params: { data?: OnFarmImplantFreezingEmbryo }) =>
          params.data
            ? labelFor(params.data[field] as string | null, params.data) || null
            : null,
      };
    };

    const yesNoColumn = (
      field: 'dtPlus' | 'crackedZona',
      headerName: string,
    ): ColDef<OnFarmImplantFreezingEmbryo> => ({
      field,
      colId: field,
      headerName,
      flex: 0.6,
      minWidth: 100,
      editable: isCellEditable,
      cellClassRules: editableCellClassRules,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: [true, false],
        formatValue: (value: boolean) => yesNo(value),
      },
      valueFormatter: (params) =>
        yesNo(params.value as boolean | null | undefined),
      filter: 'agSetColumnFilter',
      filterValueGetter: (params: { data?: OnFarmImplantFreezingEmbryo }) =>
        yesNo(params.data?.[field]) || null,
    });

    const missingLabels = (row: OnFarmImplantFreezingEmbryo) =>
      (row.missingFields ?? []).map(
        (field) =>
          columns.find((column) =>
            [field, `${field}Id`, `${field}Name`].includes(column.field ?? ''),
          )?.headerName ?? field,
      );

    const columns: ColDef<OnFarmImplantFreezingEmbryo>[] = [
      {
        field: 'embryoName',
        headerName: t('onFarmImplant.onFarmFreezing.embryo'),
        flex: 1.2,
        minWidth: 140,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => params.value ?? '—',
      },
      {
        colId: 'missingFields',
        headerName: t('onFarmImplant.onFarmFreezing.missingInfo'),
        minWidth: 160,
        flex: 1.2,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => {
          const labels = params.data ? missingLabels(params.data) : [];
          return labels.length ? labels.join(', ') : null;
        },
        cellRenderer: (params: { value?: string | null }) => {
          if (!params.value) return null;
          const tooltip = t('onFarmImplant.onFarmFreezing.missingTooltip', {
            fields: params.value,
          });
          return (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-700"
              title={tooltip}
              aria-label={tooltip}
            >
              <i className="pi pi-exclamation-triangle text-amber-500" />
              {params.value}
            </span>
          );
        },
      },
      {
        field: 'sourceType',
        headerName: t('onFarmImplant.onFarmFreezing.sourceType'),
        flex: 0.9,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        valueGetter: (params) =>
          params.data ? sourceTypeLabel(params.data.sourceType) : '',
      },
      {
        field: 'matingName',
        headerName: t('onFarmImplant.onFarmFreezing.mating'),
        flex: 1.2,
        minWidth: 140,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'donorName',
        headerName: t('onFarmImplant.onFarmFreezing.donor'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'sireName',
        headerName: t('onFarmImplant.onFarmFreezing.sire'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'accountName',
        headerName: t('onFarmImplant.onFarmFreezing.account'),
        flex: 1.2,
        minWidth: 140,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'freezeDate',
        colId: 'freezeDate',
        headerName: t('onFarmImplant.onFarmFreezing.fields.freezeDate'),
        flex: 0.9,
        minWidth: 120,
        filter: false,
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        valueGetter: (params) => params.data?.freezeDate?.slice(0, 10) ?? null,
        valueFormatter: dateOnlyFormatter,
        cellEditor: 'agDateStringCellEditor',
        cellEditorParams: { max: todayWallClockDate() },
      },
      lookupColumn(
        'freezeTechId',
        t('onFarmImplant.onFarmFreezing.fields.freezeTech'),
        options?.techOptions ?? [],
      ),
      lookupColumn(
        'freezeMethodId',
        t('onFarmImplant.onFarmFreezing.fields.freezeMethod'),
        options?.freezeMethodOptions ?? [],
      ),
      yesNoColumn('dtPlus', t('onFarmImplant.onFarmFreezing.fields.dtPlus')),
      {
        field: 'strawNumber',
        colId: 'strawNumber',
        headerName: t('onFarmImplant.onFarmFreezing.fields.strawNumber'),
        flex: 0.6,
        minWidth: 90,
        filter: 'agNumberColumnFilter',
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: { min: 1, precision: 0 },
      },
      {
        field: 'cane',
        colId: 'cane',
        headerName: t('onFarmImplant.onFarmFreezing.fields.cane'),
        flex: 0.8,
        minWidth: 110,
        filter: 'agTextColumnFilter',
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        cellEditor: 'agTextCellEditor',
        cellEditorParams: { maxLength: FREEZING_TEXT_LIMITS.cane },
      },
      lookupColumn(
        'storageLocationId',
        t('onFarmImplant.onFarmFreezing.fields.storageLocation'),
        options?.storageLocationOptions ?? [],
      ),
      {
        field: 'freezer',
        colId: 'freezer',
        headerName: t('onFarmImplant.onFarmFreezing.fields.freezer'),
        flex: 0.7,
        minWidth: 100,
        filter: 'agTextColumnFilter',
        editable: isCellEditable,
        cellClassRules: editableCellClassRules,
        cellEditor: 'agTextCellEditor',
        cellEditorParams: { maxLength: FREEZING_TEXT_LIMITS.freezer },
      },
      lookupColumn(
        'graderId',
        t('onFarmImplant.onFarmFreezing.fields.grader'),
        options?.techOptions ?? [],
      ),
      yesNoColumn(
        'crackedZona',
        t('onFarmImplant.onFarmFreezing.fields.crackedZona'),
      ),
      {
        field: 'stage',
        headerName: t('onFarmImplant.onFarmFreezing.stage'),
        flex: 0.5,
        minWidth: 70,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'grade',
        headerName: t('onFarmImplant.onFarmFreezing.grade'),
        flex: 0.5,
        minWidth: 70,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'embryoStatusName',
        headerName: t('onFarmImplant.onFarmFreezing.embryoStatus'),
        flex: 0.9,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        cellRenderer: (params: { data?: OnFarmImplantFreezingEmbryo }) =>
          params.data?.embryoStatus == null ? null : (
            <StatusBadge
              label={params.data.embryoStatusName}
              colorMap={embryoStatusColors}
            />
          ),
      },
      {
        field: 'sentFromImplantQueue',
        headerName: t('onFarmImplant.onFarmFreezing.sentFromQueue'),
        flex: 0.8,
        minWidth: 120,
        filter: 'agSetColumnFilter',
        valueGetter: (params) =>
          params.data?.sentFromImplantQueue
            ? t('onFarmImplant.onFarmFreezing.yes')
            : t('onFarmImplant.onFarmFreezing.no'),
      },
      {
        field: 'sentAt',
        headerName: t('onFarmImplant.onFarmFreezing.sentAt'),
        flex: 1,
        minWidth: 150,
        filter: false,
        valueFormatter: dateTimeFormatter,
      },
    ];

    if (readOnly) return columns;

    return [
      {
        colId: 'select',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        filter: false,
        sortable: false,
        resizable: false,
        suppressColumnsToolPanel: true,
      },
      ...columns,
    ];
  }, [options, readOnly, sourceTypeLabel, t]);
};
